// Smart Slots v2/v3 — Algoritmo de scoring de disponibilidad

// ─── Industry Rules (Fase 3) ──────────────────────────────────────────────────

export interface IndustryRule {
  /** Minimum buffer in minutes between appointments (salon settings can override upward) */
  buffer_min: number;
  /** Hard daily appointment cap per professional. null = unlimited */
  max_patients_day: number | null;
  /** Whether this industry shows a waitlist option when the day is fully booked */
  waitlist: boolean;
}

export const INDUSTRY_RULES: Record<string, IndustryRule> = {
  beauty:     { buffer_min: 5,  max_patients_day: null, waitlist: true  },
  nail:       { buffer_min: 5,  max_patients_day: null, waitlist: true  },
  barbershop: { buffer_min: 5,  max_patients_day: null, waitlist: true  },
  spa:        { buffer_min: 10, max_patients_day: null, waitlist: true  },
  fitness:    { buffer_min: 5,  max_patients_day: null, waitlist: false },
  psychology: { buffer_min: 15, max_patients_day: 8,   waitlist: false },
  nutrition:  { buffer_min: 10, max_patients_day: 10,  waitlist: false },
  veterinary: { buffer_min: 5,  max_patients_day: null, waitlist: true  },
  dental:     { buffer_min: 15, max_patients_day: null, waitlist: false },
  medical:    { buffer_min: 15, max_patients_day: 12,  waitlist: false },
};

const DEFAULT_INDUSTRY_RULE: IndustryRule = {
  buffer_min: 0,
  max_patients_day: null,
  waitlist: false,
};

export function getIndustryRule(industryType: string | null | undefined): IndustryRule {
  return (industryType && INDUSTRY_RULES[industryType]) ? INDUSTRY_RULES[industryType] : DEFAULT_INDUSTRY_RULE;
}

// ─────────────────────────────────────────────────────────────────────────────

export interface ServiceBucket {
  min: number;
  max: number;
  label: string;
}

export interface ScoredSlot {
  startMin: number;
  score: number;
  isRecommended: boolean;
  /** V3: slot has historically low fill rate → promoted as "Disponibilidad especial" */
  isDeadHour?: boolean;
  gapAnalysis: {
    totalDeadMin: number;
    totalFillableMin: number;
    gaps: { start: number; end: number; dur: number; fillable: boolean; bucket: string | null }[];
  };
}

export interface SlotParams {
  apertura: number;
  cierre: number;
  breakStart: number | null;
  breakEnd: number | null;
  totalDur: number;
  booked: { start: number; end: number }[];
  serviceBuckets: ServiceBucket[];
}

export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export function minutesToTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function mergeBlocks(blocks: { start: number; end: number }[]): { start: number; end: number }[] {
  if (!blocks.length) return [];
  const sorted = [...blocks].sort((a, b) => a.start - b.start);
  const merged = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    if (sorted[i].start <= last.end) {
      last.end = Math.max(last.end, sorted[i].end);
    } else {
      merged.push({ ...sorted[i] });
    }
  }
  return merged;
}

function isFillable(dur: number, buckets: ServiceBucket[]): boolean {
  return buckets.some(
    (b) => (dur >= b.min - 5 && dur <= b.max + 5) || dur >= 2 * b.min
  );
}

export function buildServiceBuckets(services: { duration_minutes: number }[]): ServiceBucket[] {
  const BUCKETS: ServiceBucket[] = [
    { min: 5, max: 30, label: 'Micro' },
    { min: 31, max: 60, label: 'Corto' },
    { min: 61, max: 120, label: 'Medio' },
    { min: 121, max: 180, label: 'Largo' },
  ];
  return BUCKETS.filter((b) =>
    services.some((s) => s.duration_minutes >= b.min && s.duration_minutes <= b.max)
  );
}

/**
 * V3: Dynamic buckets derived from the actual service catalog.
 * Creates ±12 min windows around each unique service duration and merges
 * overlapping windows. More precise than static buckets: a gap is only
 * considered "fillable" if it can genuinely accommodate a real service.
 */
export function buildDynamicServiceBuckets(services: { duration_minutes: number }[]): ServiceBucket[] {
  if (!services.length) return [];

  const unique = [...new Set(services.map((s) => s.duration_minutes))].sort((a, b) => a - b);

  // Build ±12 min windows, merging overlaps
  const windows: { min: number; max: number }[] = [];
  for (const dur of unique) {
    const lo = Math.max(5, dur - 12);
    const hi = dur + 12;
    const last = windows[windows.length - 1];
    if (last && lo <= last.max) {
      last.max = Math.max(last.max, hi);
    } else {
      windows.push({ min: lo, max: hi });
    }
  }

  return windows.map((w) => ({
    min: w.min,
    max: w.max,
    label:
      w.max <= 35 ? 'Micro'
      : w.max <= 72 ? 'Corto'
      : w.max <= 132 ? 'Medio'
      : 'Largo',
  }));
}

// ─── SmartSlots V3 ────────────────────────────────────────────────────────────

/**
 * Extended params for SmartSlots V3 scoring (5 dimensions).
 * All V3 fields are optional → falls back to V2 scoring when absent.
 */
export interface SlotParamsV3 extends SlotParams {
  /** D3: minutes booked this week by this professional */
  weeklyLoadMin?: number;
  /** D4: client's historically preferred time window (minutes since midnight) */
  clientPreferredRange?: { from: number; to: number } | null;
  /** D5: hour_slot (min) → historical fill rate (0–1). Source: slot_demand_stats */
  slotDemandMap?: Map<number, number>;
  /** Fase 3: industry type — used for logging/future scoring dimensions */
  industryType?: string | null;
}

/** Returns the fill rate for the closest hour_slot within `tolerance` minutes, or null. */
function findNearestDemandRate(
  map: Map<number, number>,
  slot: number,
  tolerance: number
): number | null {
  if (map.has(slot)) return map.get(slot)!;
  let best: number | null = null;
  let bestDist = Infinity;
  for (const [hourSlot, rate] of map) {
    const dist = Math.abs(hourSlot - slot);
    if (dist <= tolerance && dist < bestDist) { best = rate; bestDist = dist; }
  }
  return best;
}

/**
 * SmartSlots V3 — 5-dimension scoring.
 *
 * D1  EficienciaGap      Gap dead/fillable analysis (based on V2, with added micro-gap
 *                        penalty, zero-dead-gap bonus, and a tiered edge bonus)
 * D2  CargaDia           Day occupancy % — penalizes overloaded late slots, boosts empty days
 * D3  EquilibrioEquipo   Weekly load — underloaded pros get boost, overloaded get penalty
 * D4  PerfilCliente      Proximity to client's historically preferred hour range
 * D5  ActivacionHorasMuertas  Fill-rate < 30% → promote slot with bonus + isDeadHour flag
 *
 * Final score = clamp(D1 + D2adj + D3adj + D4adj + D5adj, 0, 100)
 */
export function calcSmartSlotsV3(params: SlotParamsV3): ScoredSlot[] {
  const {
    apertura, cierre, breakStart, breakEnd, totalDur, booked, serviceBuckets,
    weeklyLoadMin,
    clientPreferredRange,
    slotDemandMap,
  } = params;

  // ── Steps 1-3: valid slots (identical to V2) ──────────────────────────────
  const rawBlocks = [...booked];
  if (breakStart !== null && breakEnd !== null) rawBlocks.push({ start: breakStart, end: breakEnd });
  const mergedBlocks = mergeBlocks(rawBlocks);

  const startPointSet = new Set<number>();
  startPointSet.add(apertura);
  if (breakEnd !== null) startPointSet.add(breakEnd);
  for (const b of booked) startPointSet.add(b.end);

  let prevSize = 0;
  while (startPointSet.size > prevSize) {
    prevSize = startPointSet.size;
    for (const s of [...startPointSet]) {
      const endAt = s + totalDur;
      if (endAt > cierre) continue;
      if (!mergedBlocks.some((b) => s < b.end && endAt > b.start)) startPointSet.add(endAt);
    }
  }

  const validSlots: number[] = [];
  for (const s of startPointSet) {
    const endAt = s + totalDur;
    if (endAt > cierre) continue;
    if (!mergedBlocks.some((b) => s < b.end && endAt > b.start)) validSlots.push(s);
  }

  const edgePoints = new Set<number>([apertura, cierre]);
  if (breakStart !== null) edgePoints.add(breakStart);
  if (breakEnd !== null) edgePoints.add(breakEnd);
  for (const b of booked) { edgePoints.add(b.start); edgePoints.add(b.end); }

  // ── D2 pre-computation ────────────────────────────────────────────────────
  const breakDur = breakStart !== null && breakEnd !== null ? breakEnd - breakStart : 0;
  const totalAvailMin = Math.max(1, cierre - apertura - breakDur);
  const bookedMin = booked.reduce((s, b) => s + Math.max(0, b.end - b.start), 0);
  const ocupPct = bookedMin / totalAvailMin;
  const lateThreshold = apertura + totalAvailMin * 0.80;

  // ── D3 pre-computation (same adjustment applied to all slots) ─────────────
  let d3 = 0;
  if (weeklyLoadMin !== undefined) {
    if      (weeklyLoadMin < 120) d3 = +12;  // very light week
    else if (weeklyLoadMin < 240) d3 = +6;   // light
    else if (weeklyLoadMin > 600) d3 = -8;   // heavy
    else if (weeklyLoadMin > 480) d3 = -4;   // somewhat heavy
  }

  // ── Score each slot ───────────────────────────────────────────────────────
  const scored: ScoredSlot[] = validSlots.map((s) => {
    const endAt = s + totalDur;
    const simBlocks = mergeBlocks([...mergedBlocks, { start: s, end: endAt }]);

    // Gap analysis (same as V2)
    const gaps: ScoredSlot['gapAnalysis']['gaps'] = [];
    let cursor = apertura;
    for (const block of simBlocks) {
      if (block.start > cursor) {
        const gapEnd = Math.min(block.start, cierre);
        const dur = gapEnd - cursor;
        if (dur > 0) {
          const fillable = isFillable(dur, serviceBuckets);
          const bucket = serviceBuckets.find((b) => dur >= b.min - 5 && dur <= b.max + 5)?.label ?? null;
          gaps.push({ start: cursor, end: gapEnd, dur, fillable, bucket });
        }
      }
      cursor = Math.max(cursor, block.end);
    }
    if (cursor < cierre) {
      const dur = cierre - cursor;
      const fillable = isFillable(dur, serviceBuckets);
      const bucket = serviceBuckets.find((b) => dur >= b.min - 5 && dur <= b.max + 5)?.label ?? null;
      gaps.push({ start: cursor, end: cierre, dur, fillable, bucket });
    }

    const totalDeadMin    = gaps.filter((g) => !g.fillable).reduce((a, g) => a + g.dur, 0);
    const totalFillableMin = gaps.filter((g) => g.fillable).reduce((a, g) => a + g.dur, 0);
    const microDeadMin = gaps.filter((g) => !g.fillable && g.dur <= 20).reduce((a, g) => a + g.dur, 0);
    const exactEdgeFit = edgePoints.has(s) && edgePoints.has(endAt);

    // D1: efficiency gap
    const edgeBonus = exactEdgeFit ? 20 : (edgePoints.has(s) || edgePoints.has(endAt) ? 12 : 0);
    const zeroDeadGapBonus = totalDeadMin === 0 ? 10 : 0;
    const d1 = 100 - totalDeadMin * 1.5 - microDeadMin * 0.7 + totalFillableMin * 0.8 + edgeBonus + zeroDeadGapBonus;

    // D2: day load
    let d2 = 0;
    if (ocupPct > 0.80 && s > lateThreshold) d2 = -10;  // overloaded + late → penalty
    else if (ocupPct < 0.30) d2 = +5;                    // empty day → flat boost

    // D4: client preferred time range
    let d4 = 0;
    if (clientPreferredRange) {
      const { from, to } = clientPreferredRange;
      if (s >= from && s <= to) {
        const center   = (from + to) / 2;
        const halfSpan = Math.max(1, (to - from) / 2);
        const proximity = 1 - Math.abs(s - center) / halfSpan;
        d4 = Math.round(15 * Math.max(0, proximity));
      }
    }

    // D5: dead hour promotion
    let d5 = 0;
    let isDeadHour = false;
    if (slotDemandMap && slotDemandMap.size > 0) {
      const rate = findNearestDemandRate(slotDemandMap, s, 30);
      if (rate !== null) {
        if      (rate < 0.30) { d5 = +18; isDeadHour = true; }  // promote dead hours!
        else if (rate < 0.50) { d5 = +8; }
        else if (rate > 0.85) { d5 = -5; }                      // spread very popular slots
      }
    }

    const score = Math.max(0, Math.min(100, d1 + d2 + d3 + d4 + d5));

    return {
      startMin: s,
      score,
      isRecommended: false,
      isDeadHour,
      gapAnalysis: { totalDeadMin, totalFillableMin, gaps },
    };
  });

  // Mark recommended (within 10pts of max)
  if (scored.length > 0) {
    const maxScore = Math.max(...scored.map((s) => s.score));
    for (const slot of scored) slot.isRecommended = slot.score >= maxScore - 10;
  }

  return scored.sort((a, b) => a.startMin - b.startMin);
}

// ─── SmartSlots V2 (legacy — kept for reference) ─────────────────────────────

export function calcSmartSlotsV2(params: SlotParams): ScoredSlot[] {
  const { apertura, cierre, breakStart, breakEnd, totalDur, booked, serviceBuckets } = params;

  // Step 1: Merge break + booked into unified blocked blocks
  const rawBlocks = [...booked];
  if (breakStart !== null && breakEnd !== null) {
    rawBlocks.push({ start: breakStart, end: breakEnd });
  }
  const mergedBlocks = mergeBlocks(rawBlocks);

  // Step 2: Natural start points only
  const startPointSet = new Set<number>();
  startPointSet.add(apertura);
  if (breakEnd !== null) startPointSet.add(breakEnd);
  for (const b of booked) startPointSet.add(b.end);

  // Step 2.5: Chaining — each valid slot's end becomes a new start point
  let prevSize = 0;
  while (startPointSet.size > prevSize) {
    prevSize = startPointSet.size;
    for (const s of [...startPointSet]) {
      const endAt = s + totalDur;
      if (endAt > cierre) continue;
      const overlaps = mergedBlocks.some((b) => s < b.end && endAt > b.start);
      if (!overlaps) startPointSet.add(endAt);
    }
  }

  // Step 3: Filter valid slots
  const validSlots: number[] = [];
  for (const s of startPointSet) {
    const endAt = s + totalDur;
    if (endAt > cierre) continue;
    const overlaps = mergedBlocks.some((b) => s < b.end && endAt > b.start);
    if (!overlaps) validSlots.push(s);
  }

  // Edge points for bonus scoring
  const edgePoints = new Set<number>([apertura, cierre]);
  if (breakStart !== null) edgePoints.add(breakStart);
  if (breakEnd !== null) edgePoints.add(breakEnd);
  for (const b of booked) { edgePoints.add(b.start); edgePoints.add(b.end); }

  // Steps 4 & 5: Score each valid slot
  const scored: ScoredSlot[] = validSlots.map((s) => {
    const endAt = s + totalDur;
    const simBlocks = mergeBlocks([...mergedBlocks, { start: s, end: endAt }]);

    // Compute gaps between apertura and cierre
    const gaps: ScoredSlot['gapAnalysis']['gaps'] = [];
    let cursor = apertura;

    for (const block of simBlocks) {
      if (block.start > cursor) {
        const gapEnd = Math.min(block.start, cierre);
        const dur = gapEnd - cursor;
        if (dur > 0) {
          const fillable = isFillable(dur, serviceBuckets);
          const bucket = serviceBuckets.find((b) => dur >= b.min - 5 && dur <= b.max + 5)?.label ?? null;
          gaps.push({ start: cursor, end: gapEnd, dur, fillable, bucket });
        }
      }
      cursor = Math.max(cursor, block.end);
    }
    // Trailing gap
    if (cursor < cierre) {
      const dur = cierre - cursor;
      const fillable = isFillable(dur, serviceBuckets);
      const bucket = serviceBuckets.find((b) => dur >= b.min - 5 && dur <= b.max + 5)?.label ?? null;
      gaps.push({ start: cursor, end: cierre, dur, fillable, bucket });
    }

    const totalDeadMin = gaps.filter((g) => !g.fillable).reduce((acc, g) => acc + g.dur, 0);
    const totalFillableMin = gaps.filter((g) => g.fillable).reduce((acc, g) => acc + g.dur, 0);
    const edgeBonus = edgePoints.has(s) || edgePoints.has(endAt) ? 15 : 0;
    const score = Math.max(0, Math.min(100, 100 - totalDeadMin * 1.5 + totalFillableMin * 0.8 + edgeBonus));

    return {
      startMin: s,
      score,
      isRecommended: false,
      gapAnalysis: { totalDeadMin, totalFillableMin, gaps },
    };
  });

  // Step 6: Mark recommended (within 10pts of max)
  if (scored.length > 0) {
    const maxScore = Math.max(...scored.map((s) => s.score));
    for (const slot of scored) {
      slot.isRecommended = slot.score >= maxScore - 10;
    }
  }

  // Step 7: Sort by startMin ASC
  return scored.sort((a, b) => a.startMin - b.startMin);
}
